import { useState, useRef, useEffect, useCallback } from 'react';

const AUDIO_SRC = 'https://cdna.lucendoelite.com/assets/music/home.mpeg';

export default function AudioPlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Initialize audio element once
  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio(AUDIO_SRC);
      audio.loop = true;
      audio.volume = 0.4;
      audio.preload = 'auto';
      audioRef.current = audio;
    }

    // Play on first user interaction anywhere on the page
    const playOnInteraction = async () => {
      if (audioRef.current && !isPlaying) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch {
          // Still blocked, will try again on next interaction
        }
      }
      // If playback succeeded, stop listening — music is already playing
      if (audioRef.current && !audioRef.current.paused) {
        cleanup();
      }
    };

    const events = ['click', 'touchstart', 'keydown', 'scroll'] as const;
    events.forEach((event) => document.addEventListener(event, playOnInteraction, { once: false, passive: true }));

    const cleanup = () => {
      events.forEach((event) => document.removeEventListener(event, playOnInteraction));
    };

    return () => {
      cleanup();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on browser tab close / navigation away from SPA
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const togglePlay = useCallback(async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch {
        // Playback failed
      }
    }
  }, [isPlaying]);

  return (
    <>
      {/* Floating Music Toggle Button */}
      <button
        onClick={togglePlay}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="fixed bottom-8 left-8 z-40 w-12 h-12 rounded-full bg-luxury-charcoal/90 backdrop-blur-sm border border-luxury-gold/30 flex items-center justify-center cursor-pointer transition-all duration-300 hover:border-luxury-gold/60 hover:bg-luxury-charcoal group shadow-lg"
        aria-label={isPlaying ? 'Pause music' : 'Play music'}
      >
        {/* Pulsing ring when playing */}
        {isPlaying && (
          <span className="absolute inset-0 rounded-full border border-luxury-gold/40 animate-ping opacity-30"></span>
        )}

        <div className={`${isPlaying ? 'animate-pulse' : ''}`}>
          {isPlaying ? (
            <i className="ri-music-fill text-luxury-gold text-xl"></i>
          ) : (
            <i className="ri-music-line text-luxury-gold/60 text-xl group-hover:text-luxury-gold transition-colors duration-300"></i>
          )}
        </div>

        {/* Subtle equalizer bars when playing */}
        {isPlaying && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-end gap-[2px] pb-0.5">
            <span className="w-[2px] bg-luxury-gold/70 rounded-full animate-[equalizer_0.8s_ease-in-out_infinite]" style={{ height: '4px' }}></span>
            <span className="w-[2px] bg-luxury-gold/70 rounded-full animate-[equalizer_0.6s_ease-in-out_infinite_0.1s]" style={{ height: '7px' }}></span>
            <span className="w-[2px] bg-luxury-gold/70 rounded-full animate-[equalizer_0.5s_ease-in-out_infinite_0.2s]" style={{ height: '5px' }}></span>
            <span className="w-[2px] bg-luxury-gold/70 rounded-full animate-[equalizer_0.7s_ease-in-out_infinite_0.3s]" style={{ height: '3px' }}></span>
          </div>
        )}
      </button>

      {/* Tooltip */}
      <div
        className={`fixed bottom-24 left-8 z-40 px-3 py-1.5 rounded-md bg-luxury-charcoal/90 backdrop-blur-sm border border-luxury-gold/20 text-luxury-cream text-xs font-sans tracking-luxury whitespace-nowrap transition-all duration-300 pointer-events-none ${
          showTooltip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        {isPlaying ? 'Music playing' : 'Music paused'}
      </div>

      {/* Equalizer animation keyframes */}
      <style>{`
        @keyframes equalizer {
          0%, 100% { height: 3px; }
          50% { height: 8px; }
        }
      `}</style>
    </>
  );
}